import { useEffect } from 'react';
import { Euler, Object3D, Vector3 } from 'three';

import type { EnhancedGLTF, LoadedCharacterMesh } from '@cartwhl/3d/gltf/types';
import {
  applyFaceExpression,
  applyShowSkeleton,
  applySkinHex,
  disableFrustumCulled,
  hideMeshMaterials,
  isMhrScene,
  turnOnShadows,
} from '@cartwhl/3d/gltf/utils';

import useCharacterMeshWithCredentials, {
  type CharacterMeshFormat,
  type LoadFBXMesh,
  type TransformLoadedMesh,
} from '../hooks/useCharacterMeshWithCredentials';

export type CharacterRef = EnhancedGLTF | LoadedCharacterMesh;

/**
 * Character component that renders a character model (GLB/GLTF or FBX).
 * This is a pure component that shouldn't access stores.
 * Please refer to <ActiveCharacter> component if you want to display the current project's character.
 * When meshPath is a blob URL, pass meshFormat so the correct loader is used.
 */
export default function Character({
  characterID,
  meshPath,
  meshFormat,
  rotation,
  position,
  skinHex,
  faceExpression,
  showSkeleton,
  onSceneReady,
  onCharacterLoading,
  transformLoadedMesh,
  loadFBX,
  identityPath,
}: {
  characterID: string;
  meshPath: string;
  /** Required when meshPath is a blob URL (no file extension). */
  meshFormat?: CharacterMeshFormat;
  rotation?: Euler;
  position?: Vector3;
  skinHex?: string;
  faceExpression?: string;
  showSkeleton?: boolean;
  onSceneReady?: (scene: Object3D) => void;
  onCharacterLoading?: (loading: boolean) => void;
  transformLoadedMesh?: TransformLoadedMesh;
  loadFBX?: LoadFBXMesh;
  /** Optional URL to a static morph identity JSON; applied once before render (MHR rigs only). */
  identityPath?: string;
}) {
  const meshData = useCharacterMeshWithCredentials(
    meshPath,
    onCharacterLoading,
    {
      format: meshFormat,
      modifiers: [hideMeshMaterials, turnOnShadows, disableFrustumCulled],
      transformLoadedMesh,
      loadFBX,
      identityPath,
    },
  );

  // Notify parent when scene is ready
  useEffect(() => {
    if (meshData?.scene && onSceneReady) {
      onSceneReady(meshData.scene as Object3D);
    }
  }, [meshData, onSceneReady]);

  useEffect(() => {
    if (meshData) {
      applySkinHex(skinHex, characterID)(meshData);
    }
  }, [characterID, meshData, skinHex]);

  useEffect(() => {
    if (meshData && faceExpression) {
      applyFaceExpression(faceExpression)(meshData);
    }
  }, [faceExpression, meshData]);

  useEffect(() => {
    if (meshData) {
      applyShowSkeleton(meshData, showSkeleton ?? false);
    }
  }, [showSkeleton, meshData]);

  if (!meshData) return null;

  const resolvedRotation = isMhrScene(meshData.scene) ? undefined : rotation;

  return (
    <primitive
      object={meshData.scene}
      rotation={resolvedRotation}
      position={position}
    />
  );
}
